// src/pages/maintenance/MaintenancePartsPage.js
// Spare parts/consumables inventory — stock levels, low-stock flag, and a stock-event ledger
// (received/adjustment/returned; "used_in_wo" events are written automatically on WO completion).
// See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import maintenanceService from '../../services/maintenanceService';
import ConfirmModal from '../../components/ConfirmModal';
import MaintenanceNav from '../../components/MaintenanceNav';

const fmtQty = (n, uom) => n != null ? `${parseFloat(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${uom || ''}`.trim() : '—';
const fmtMoney = (n) => n != null ? `$${parseFloat(n).toFixed(2)}` : '—';

function PartModal({ part, onSave, onClose }) {
  const [form, setForm] = useState({
    name: part?.name || '', partNumber: part?.partNumber || '', category: part?.category || '',
    unitOfMeasure: part?.unitOfMeasure || 'each', currentStock: part?.currentStock ?? 0,
    minStockThreshold: part?.minStockThreshold ?? '', unitCost: part?.unitCost ?? '',
    storageLocation: part?.storageLocation || '', supplierName: part?.supplierName || '', notes: part?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        currentStock: form.currentStock !== '' ? parseFloat(form.currentStock) : 0,
        minStockThreshold: form.minStockThreshold !== '' ? parseFloat(form.minStockThreshold) : null,
        unitCost: form.unitCost !== '' ? parseFloat(form.unitCost) : null,
      };
      const result = part?.id ? await maintenanceService.updatePart(part.id, payload) : await maintenanceService.createPart(payload);
      onSave(result);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save part.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{part?.id ? 'Edit' : 'New'} Part</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Name <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Part Number</label>
                  <input className="form-control form-control-sm" value={form.partNumber} onChange={e => set('partNumber', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Category</label>
                  <input className="form-control form-control-sm" value={form.category} onChange={e => set('category', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Unit of Measure</label>
                  <input className="form-control form-control-sm" value={form.unitOfMeasure} onChange={e => set('unitOfMeasure', e.target.value)} />
                </div>
                {!part?.id && (
                  <div className="col-md-4">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Starting Stock</label>
                    <input className="form-control form-control-sm" value={form.currentStock} onChange={e => set('currentStock', e.target.value)} />
                  </div>
                )}
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Min Stock Threshold</label>
                  <input className="form-control form-control-sm" value={form.minStockThreshold} onChange={e => set('minStockThreshold', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Unit Cost ($)</label>
                  <input className="form-control form-control-sm" value={form.unitCost} onChange={e => set('unitCost', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Storage Location</label>
                  <input className="form-control form-control-sm" value={form.storageLocation} onChange={e => set('storageLocation', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Supplier</label>
                  <input className="form-control form-control-sm" value={form.supplierName} onChange={e => set('supplierName', e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save Part'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function StockEventModal({ part, onSave, onClose }) {
  const [eventType, setEventType] = useState('received');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState(part.unitCost || '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (quantity === '') { setError('Quantity is required.'); return; }
    setSaving(true); setError('');
    try {
      await maintenanceService.addPartStockEvent(part.id, {
        eventType, quantity: parseFloat(quantity),
        unitCost: unitCost !== '' ? parseFloat(unitCost) : null,
        notes,
      });
      onSave();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to record stock event.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Stock Event — {part.name}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="mb-2">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Event Type</label>
                <select className="form-select form-select-sm" value={eventType} onChange={e => setEventType(e.target.value)}>
                  <option value="received">Received</option>
                  <option value="adjustment">Adjustment (+/-)</option>
                  <option value="returned">Returned</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Quantity {eventType === 'adjustment' ? '(signed, e.g. -2)' : ''}</label>
                <input className="form-control form-control-sm" value={quantity} onChange={e => setQuantity(e.target.value)} />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Unit Cost ($)</label>
                <input className="form-control form-control-sm" value={unitCost} onChange={e => setUnitCost(e.target.value)} />
              </div>
              <div>
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes</label>
                <textarea className="form-control form-control-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Record Event'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function MaintenancePartsPage() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [showPartModal, setShowPartModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stockEventTarget, setStockEventTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (lowStockOnly) params.lowStock = 'true';
      const data = await maintenanceService.getParts(params);
      setParts(data);
    } finally {
      setLoading(false);
    }
  }, [search, lowStockOnly]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async () => {
    await maintenanceService.deletePart(archiveTarget.id);
    setArchiveTarget(null);
    load();
  };

  return (
    <>
      <MaintenanceNav />
      <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Parts Inventory</h4>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowPartModal(true); }}>+ New Part</button>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <input className="form-control form-control-sm" placeholder="Search parts…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="col-md-4 d-flex align-items-center">
          <div className="form-check">
            <input type="checkbox" className="form-check-input" id="lowStock" checked={lowStockOnly} onChange={e => setLowStockOnly(e.target.checked)} />
            <label className="form-check-label" htmlFor="lowStock" style={{ fontSize: 13 }}>Low stock only</label>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : parts.length === 0 ? (
        <div className="text-center py-5 text-muted">No parts found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr><th>Name</th><th>Part #</th><th>Stock</th><th>Unit Cost</th><th>Storage</th><th style={{ width: 1 }}></th></tr>
            </thead>
            <tbody>
              {parts.map(p => {
                const low = p.minStockThreshold != null && Number(p.currentStock) <= Number(p.minStockThreshold);
                return (
                  <tr key={p.id}>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>{p.name} {low && <span className="badge bg-danger ms-2" style={{ fontSize: 10 }}>Low Stock</span>}</td>
                    <td style={{ fontSize: 13 }}>{p.partNumber || '—'}</td>
                    <td style={{ fontSize: 13 }}>{fmtQty(p.currentStock, p.unitOfMeasure)}</td>
                    <td style={{ fontSize: 13 }}>{fmtMoney(p.unitCost)}</td>
                    <td style={{ fontSize: 13 }}>{p.storageLocation || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="d-flex justify-content-end gap-1">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setStockEventTarget(p)}>Stock Event</button>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditing(p); setShowPartModal(true); }}>Edit</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setArchiveTarget(p)}>Archive</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showPartModal && (
        <PartModal part={editing} onSave={() => { setShowPartModal(false); setEditing(null); load(); }} onClose={() => { setShowPartModal(false); setEditing(null); }} />
      )}
      {stockEventTarget && (
        <StockEventModal part={stockEventTarget} onSave={() => { setStockEventTarget(null); load(); }} onClose={() => setStockEventTarget(null)} />
      )}

      <ConfirmModal
        show={!!archiveTarget}
        title="Archive Part"
        message={`Archive "${archiveTarget?.name}"?`}
        confirmLabel="Archive"
        onConfirm={handleArchive}
        onCancel={() => setArchiveTarget(null)}
      />
      </div>
    </>
  );
}
